/**
 * Custom domain hooks (task 850).
 *
 * Report: `project/reports/platform-custom-domains-account-endpoints.md`.
 *
 * One query backs the whole section: the account's domains AND the live plan
 * gate, resolved together server-side so the two can never disagree about
 * whether another domain may be added. Reads go through the app's service layer
 * (`customDomainsApi`), never a raw fetch.
 *
 * **Polling is the interesting part.** A registered domain is not live until the
 * customer edits their DNS and the platform's verifier sees the CNAME and the
 * managed certificate. That is a background lifecycle with no push channel, so
 * the list re-reads itself — but only while there is something to wait for. The
 * timer runs when any row is `pending_dns` or `verifying`, stops the moment
 * every row is `active` or `failed`, and React Query stops it on unmount, so an
 * account whose domains have all settled makes no requests at all.
 *
 * Mutations deliberately carry no `onError`: the section renders the API's own
 * refusal (`HOSTNAME_TAKEN`, `APEX_NOT_SUPPORTED`, the entitlement codes) inline
 * under the input, which only the caller can place.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { customDomainsApi } from '@/services/api';
import { CUSTOM_DOMAIN_STATUS_POLL_MS } from '@/config';
import {
  isCustomDomainSettling,
  type CustomDomain,
  type CustomDomainEntitlement,
  type CustomDomainsView,
} from '@/types/customDomain';

export const CUSTOM_DOMAINS_QUERY_KEY = ['customDomains'] as const;

/**
 * The gate applied whenever the section has no answer yet. Fails CLOSED: with
 * nothing resolved the add form stays disabled, which is the same posture the
 * server takes.
 */
const UNRESOLVED_ENTITLEMENT: CustomDomainEntitlement = {
  entitled: false,
  canAdd: false,
  limit: null,
  unlimited: false,
  used: 0,
  reason: null,
  planSlug: null,
};

const NO_DOMAINS: CustomDomain[] = [];

/** True while any row in the list is still waiting on DNS or a certificate. */
export const hasSettlingCustomDomains = (view: CustomDomainsView | undefined): boolean =>
  (view?.domains ?? []).some(isCustomDomainSettling);

/**
 * The account's custom domains and its plan gate.
 *
 * Unlike the environments list, a read failure is NOT flattened into "you have
 * none" — a domain the customer has already pointed DNS at is live traffic, and
 * silently showing an empty list would invite them to register it again. The
 * error surfaces so the section can say it could not load and offer a retry.
 */
export const useCustomDomains = (options?: { enabled?: boolean }) => {
  const query = useQuery({
    queryKey: CUSTOM_DOMAINS_QUERY_KEY,
    queryFn: () => customDomainsApi.list().then((res) => res.data.data),
    enabled: options?.enabled !== false,
    // Poll only while something is still settling; stop as soon as it is not.
    refetchInterval: (q) =>
      hasSettlingCustomDomains(q.state.data) ? CUSTOM_DOMAIN_STATUS_POLL_MS : false,
  });

  return {
    ...query,
    /** Always an array, newest first, exactly as the server ordered it. */
    domains: query.data?.domains ?? NO_DOMAINS,
    entitlement: query.data?.entitlement ?? UNRESOLVED_ENTITLEMENT,
  };
};

/**
 * Register a hostname. Resolves with the new record (status `pending_dns`,
 * carrying the CNAME target); rejects with the API's enhanced error so the form
 * can read `status` and `response.code`.
 */
export const useCreateCustomDomain = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (hostname: string) =>
      customDomainsApi.create(hostname).then((res) => res.data.data),
    onSuccess: () => {
      // Refetch rather than append: the new row restarts the poll, and the
      // entitlement's `used` count moves with it.
      void queryClient.invalidateQueries({ queryKey: CUSTOM_DOMAINS_QUERY_KEY });
    },
  });
};

/**
 * Stop serving a hostname. The platform deletes the edge tenant before the
 * record — tenants bill while they exist — so a rejection here means the domain
 * is still registered and the caller should retry.
 */
export const useRemoveCustomDomain = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (hostname: string) =>
      customDomainsApi.remove(hostname).then((res) => res.data.data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: CUSTOM_DOMAINS_QUERY_KEY });
    },
  });
};

/**
 * Every hostname the account is CURRENTLY SERVING ON, oldest first (task 857).
 *
 * Only `active` rows are here, and that is the whole gate: a domain that has not
 * finished DNS or certificate issuance answers nothing, so offering its address
 * anywhere in the app would hand a customer a URL that fails. An account with
 * none gets an empty array, which is what lets every connection surface render
 * exactly as it did before custom domains existed.
 *
 * **Oldest first, deliberately** (task 850's decision, kept): the first entry is
 * the address the app leads with, and sorting by age means it does not move
 * every time another domain is attached. Later entries are alternatives — a
 * domain aliases the account's whole surface, so they all reach the same
 * services and any of them is a correct address.
 */
export const useActiveCustomDomainHostnames = (options?: { enabled?: boolean }): string[] => {
  const { domains } = useCustomDomains(options);
  return domains
    .filter((domain) => domain.status === 'active')
    .sort((a, b) => a.createdAt.localeCompare(b.createdAt))
    .map((domain) => domain.hostname);
};

/**
 * The account's ACTIVE custom domain, if it has one — the hostname public URLs
 * are shown in. With several active, the oldest wins so the address the app
 * displays does not move every time another domain is attached.
 */
export const useActiveCustomDomain = (options?: { enabled?: boolean }): string | null =>
  useActiveCustomDomainHostnames(options)[0] ?? null;
